import React from 'react';
import classNames from 'classnames';
import { Box, useScreenSize } from '@stitch-fix/mode-react';
import { type CloudinaryImageFields } from '../CloudinaryImageWrapper';
import styles from './style.module.scss';
import { useStickyCtaContext } from '../StickyCta/useStickyCtaContext';
import type { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
import { MediaItem } from './MediaItem';
import { useAutoAdvanceTimer } from '../GalleryHero/useAutoAdvanceTimer';
import { ContentItems, ContentItemsProps } from './ContentItems';
import { CloudinaryPicture } from '../CSCloudinaryAsset/Picture';

type HeroItem = {
  media: {
    mobile: CloudinaryImageFields;
    desktop: CloudinaryImageFields;
  };
  cta: {
    title: string;
    href: string;
  };
  title: ContentStackJSONRichText;
  subtitle: ContentStackJSONRichText;
  fontColor: 'white' | 'gray-16' | 'blue-45';
  contentBackground: 'none' | 'white';
  buttonColor: string;
};

export type HeroProps = {
  /**
   * @deprecated `title` is now passed in as part of the `items` prop.
   */
  title: ContentStackJSONRichText;
  /**
   * @deprecated `subtitle` is now passed in as part of the `items` prop.
   */
  subtitle: ContentStackJSONRichText;
  /**
   * @deprecated `fontColor` is now passed in as part of the `items` prop.
   */
  fontColor: 'white' | 'gray-16' | 'blue-45';
  /**
   * @deprecated `cta` is now passed in as part of the `items` prop.
   */
  cta: {
    title: string;
    href: string;
  };
  /**
   * @deprecated `media` is now passed in as part of the `items` prop.
   */
  media: {
    mobile: CloudinaryImageFields;
    desktop: CloudinaryImageFields;
  };
  /**
   * @deprecated `buttonColor` is now passed in as part of the `items` prop.
   */
  buttonColor: string;
  /**
   * @deprecated `contentBackground` is now passed in as part of the `items` prop.
   */
  contentBackground: 'none' | 'white';
  mobileLayoutVariant: 'mediaFirst' | 'contentFirst' | 'contentMediaStacked'; // contentMediaStacked is the default style for desktop and mobile
  layout?: 'split' | 'full';
  alignContent?: 'left' | 'right' | 'bottom' | 'top' | 'center';
  items?: HeroItem[];
  mobileAlignContent?: 'left' | 'right' | 'bottom' | 'top' | 'center';
};

export const Hero = ({
  title,
  subtitle,
  cta,
  media,
  fontColor,
  contentBackground,
  buttonColor,
  items = [],
  layout = 'full',
  alignContent = 'center',
  mobileLayoutVariant = 'contentMediaStacked',
  mobileAlignContent = 'center',
  verticalSpacing,
}: HeroProps & ModularComponentProps) => {
  const screenSize = useScreenSize();
  const contentAlignment =
    screenSize === 'sm' || screenSize === 'md'
      ? mobileAlignContent
      : alignContent;
  const { setStickyContainerTarget } = useStickyCtaContext();

  const { activeSlideIndex, shouldAnimate } = useAutoAdvanceTimer({
    totalItems: items.length,
  });

  const renderContent = () => {
    // If there are no items then we fallback to to the deprecated top level
    // content props
    let contentItems: ContentItemsProps['items'] = items;

    if (items.length === 0) {
      contentItems = [
        {
          title,
          subtitle,
          cta,
          fontColor,
          contentBackground,
          buttonColor,
        },
      ];
    }

    return (
      <ContentItems items={contentItems} activeSlideIndex={activeSlideIndex} />
    );
  };

  const renderMedia = () => {
    // If there are no items then we fallback to to the deprecated top level
    // media props
    if (items.length === 0) {
      return (
        <CloudinaryPicture
          alt={media.desktop.alt}
          sources={[
            {
              minWidth: 0,
              cloudinaryAsset: media.mobile.cloudinaryAsset,
            },
            {
              minWidth: 560,
              cloudinaryAsset: media.desktop.cloudinaryAsset,
            },
          ]}
          imageProps={{
            fetchPriority: 'high',
          }}
        />
      );
    }

    return items.map((item, index) => (
      <MediaItem
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        slideIndex={index}
        activeSlideIndex={activeSlideIndex}
        media={item.media}
        initialAnimate={shouldAnimate}
      />
    ));
  };

  return (
    <Box mt={verticalSpacing}>
      <div
        data-testid="container"
        className={classNames(styles.container, styles[contentAlignment], {
          [styles.mediaFirst]: mobileLayoutVariant === 'mediaFirst',
          [styles.contentFirst]: mobileLayoutVariant === 'contentFirst',
          [styles.splitView]: layout === 'split',
          [styles.fullView]: layout === 'full',
        })}
        ref={setStickyContainerTarget}
      >
        <div className={classNames(styles.media)}>{renderMedia()}</div>
        {renderContent()}
      </div>
    </Box>
  );
};
